import { type ReactNode } from 'react';
import { cn } from '@/components/ui';

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-4 border-b border-border-subtle pb-4">
      <div className="min-w-0">
        {eyebrow && (
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-text-tertiary">{eyebrow}</div>
        )}
        <h1 className="truncate text-xl font-semibold tracking-tight text-text-primary">{title}</h1>
        {description && <p className="mt-1 text-sm text-text-secondary">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Section({
  title,
  actions,
  children,
  dense = false,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  dense?: boolean;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border border-border-subtle bg-surface-0', dense ? 'p-3' : 'p-4', className)}>
      {(title || actions) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {typeof title === 'string' ? <h2 className="text-sm font-semibold text-text-primary">{title}</h2> : title}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Grid({
  cols = 4,
  children,
  className,
}: {
  cols?: 2 | 3 | 4 | 6;
  children: ReactNode;
  className?: string;
}) {
  const classes: Record<number, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
    6: 'sm:grid-cols-3 lg:grid-cols-6',
  };
  return <div className={cn('grid grid-cols-1 gap-4', classes[cols], className)}>{children}</div>;
}

export function DataTable<T>({
  columns,
  rows,
  onRowClick,
  empty = 'No data',
}: {
  columns: { key: string; label: ReactNode; width?: string; render: (row: T) => ReactNode }[];
  rows: T[];
  onRowClick?: (row: T) => void;
  empty?: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border-subtle bg-surface-1 px-3 py-8 text-center text-xs text-text-tertiary">
        {empty}
      </div>
    );
  }

  return (
    <table className="w-full table-fixed text-left text-xs">
      <thead className="sticky top-0 z-10">
        <tr className="border-b border-border-subtle text-text-tertiary">
          {columns.map((c) => (
            <th
              key={c.key}
              className="bg-surface-0 px-2 py-2 text-[10px] font-medium uppercase tracking-wider"
              style={{ width: c.width }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={i}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              'border-b border-border-subtle text-text-secondary',
              onRowClick && 'cursor-pointer hover:bg-surface-1',
            )}
          >
            {columns.map((c) => (
              <td key={c.key} className="px-2 py-2 align-middle">
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-text-primary">{value}</span>
      {sub && <span className="text-[11px] text-text-tertiary">{sub}</span>}
    </div>
  );
}
